import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Paper,
  Typography,
  Stack,
  CircularProgress,
  Alert,
  Button,
  alpha,
  Chip,
  IconButton,
  TextField,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
} from '@mui/material';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
} from 'recharts';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { format, startOfDay, endOfDay, differenceInMinutes, subDays, addDays } from 'date-fns';
import RefreshIcon from '@mui/icons-material/Refresh';
import FilterListIcon from '@mui/icons-material/FilterList';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ReplayIcon from '@mui/icons-material/Replay';
import { ScheduledCall, CallMetrics } from '../types';
import { callSchedulingApi } from '../services/api';
import AgentSelect from './AgentSelect';
import PhoneNumberSelect from './PhoneNumberSelect';

// Colors for charts
const CHART_COLORS = ['#2196f3', '#4caf50', '#f44336', '#ff9800', '#9c27b0', '#795548'];

interface DateRange {
  startDate: Date;
  endDate: Date;
}

const CallAnalytics: React.FC = () => {
  const [calls, setCalls] = useState<ScheduledCall[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [filtersVisible, setFiltersVisible] = useState(true);
  
  // Initialize dateRange from URL parameters
  const [dateRange, setDateRange] = useState<DateRange>(() => {
    const params = new URLSearchParams(window.location.search);
    return {
      startDate: params.get('start_date') ? new Date(String(params.get('start_date'))) : subDays(new Date(), 7),
      endDate: params.get('end_date') ? new Date(String(params.get('end_date'))) : new Date()
    };
  });

  // Simplified filters - removed date filters since they're handled by dateRange
  const [filters, setFilters] = useState({
    status: '',
    agent_id: '',
    from_number: '',
    to_number: '',
    is_reminder: '',
  });

  // Metrics state
  const [metrics, setMetrics] = useState({
    totalCalls: 0,
    completedCalls: 0,
    failedCalls: 0,
    averageCallDuration: 0,
    satisfactionScore: 0,
    automationStats: {
      totalTasks: 0,
      totalMessages: 0,
      totalActions: 0,
      successRate: 0,
    },
  });

  // Chart data state
  const [chartData, setChartData] = useState({
    callVolume: [] as any[],
    callDurations: [] as any[],
    satisfactionTrend: [] as any[],
    statusDistribution: [] as any[],
    actionsTaken: [] as any[],
  });

  // Update URL when date range changes
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    
    if (dateRange.startDate) {
      params.set('start_date', dateRange.startDate.toISOString());
    } else {
      params.delete('start_date');
    }
    
    if (dateRange.endDate) {
      params.set('end_date', dateRange.endDate.toISOString());
    } else {
      params.delete('end_date');
    }

    // Update URL without reloading
    const newUrl = `${window.location.pathname}${params.toString() ? '?' + params.toString() : ''}`;
    window.history.replaceState({}, '', newUrl);
  }, [dateRange]);

  // Handle URL parameter changes
  useEffect(() => {
    const handleUrlChange = () => {
      const params = new URLSearchParams(window.location.search);
      setDateRange({
        startDate: params.get('start_date') ? new Date(String(params.get('start_date'))) : subDays(new Date(), 7),
        endDate: params.get('end_date') ? new Date(String(params.get('end_date'))) : new Date()
      });
    };

    window.addEventListener('popstate', handleUrlChange);
    return () => window.removeEventListener('popstate', handleUrlChange);
  }, []);

  // Fetch data when component mounts or when dateRange/filters change
  useEffect(() => {
    fetchData();
  }, [dateRange, filters]);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Prepare filter parameters including date range
      const filterParams: any = {};
      
      // Add date range filters
      if (dateRange.startDate) {
        filterParams.from_dt = format(startOfDay(dateRange.startDate), "yyyy-MM-dd'T'HH:mm:ss'Z'");
      }
      if (dateRange.endDate) {
        filterParams.to_dt = format(endOfDay(dateRange.endDate), "yyyy-MM-dd'T'HH:mm:ss'Z'");
      }
      
      // Add other filters
      if (filters.status) filterParams.status = filters.status;
      if (filters.agent_id) filterParams.agent_id = filters.agent_id;
      if (filters.from_number) filterParams.from_number = filters.from_number;
      if (filters.to_number) filterParams.to_number = filters.to_number;

      console.log('Fetching analytics data with filters:', filterParams);
      const response = await callSchedulingApi.getScheduledCalls(filterParams);
      
      if (response && response.scheduled_calls) {
        const processedCalls = processCallsData(response.scheduled_calls);
        setCalls(processedCalls);
        calculateMetrics(processedCalls);
        generateChartData(processedCalls);
      }
    } catch (err: any) {
      console.error('Error fetching data:', err);
      setError(err.message || 'Failed to load analytics data');
    } finally {
      setLoading(false);
    }
  };

  const processCallsData = (calls: ScheduledCall[]): ScheduledCall[] => {
    return calls.map(call => {
      const processedCall = { ...call };
      
      // Handle metrics parsing with better error handling
      if (typeof processedCall.metrics === 'string') {
        try {
          processedCall.metrics = JSON.parse(processedCall.metrics);
        } catch (err) {
          console.error('Error parsing metrics for call:', call.id, err);
          processedCall.metrics = null;
        }
      }
      
      // Validate metrics structure
      if (processedCall.metrics && typeof processedCall.metrics === 'object') {
        // Ensure numeric fields are actually numbers
        if (processedCall.metrics.caller_satisfaction !== undefined) {
          const satisfaction = Number(processedCall.metrics.caller_satisfaction);
          if (!isNaN(satisfaction)) {
            processedCall.metrics.caller_satisfaction = satisfaction;
          } else {
            processedCall.metrics.caller_satisfaction = undefined;
          }
        }
      }
      
      return processedCall;
    });
  };

  const calculateMetrics = (calls: ScheduledCall[]) => {
    const completed = calls.filter(call => call.call_placed).length;
    const failed = calls.filter(call => call.call_attempted && !call.call_placed).length;
    
    let totalDuration = 0;
    let totalSatisfaction = 0;
    let satisfactionCount = 0;
    let totalTasks = 0;
    let totalMessages = 0;
    let totalActions = 0;
    let successfulAutomations = 0;
    let totalAutomations = 0;
    let callsWithDuration = 0;

    calls.forEach(call => {
      const metrics = call.metrics;
      if (metrics) {
        // Call duration with improved parsing
        if (metrics.call_duration) {
          const duration = parseDuration(metrics.call_duration);
          if (duration > 0) {
            totalDuration += duration;
            callsWithDuration++;
          }
        }

        // Satisfaction score with validation
        if (typeof metrics.caller_satisfaction === 'number' && !isNaN(metrics.caller_satisfaction)) {
          totalSatisfaction += metrics.caller_satisfaction;
          satisfactionCount++;
        }

        // Automation stats with improved success detection
        if (metrics.auto_tasks?.extracted_tasks) {
          totalTasks += metrics.auto_tasks.extracted_tasks.length;
          totalAutomations += metrics.auto_tasks.extracted_tasks.length;
          
          // Better success detection for tasks
          const successfulTasks = metrics.auto_tasks.api_results?.filter((r: any) => 
            r.success === true || 
            (typeof r.success === 'object' && r.success?.success === true) ||
            (typeof r === 'object' && r.task?.id) // Task has an ID means it was created
          ).length || 0;
          successfulAutomations += successfulTasks;
        }
        
        if (metrics.auto_text_messages?.extracted_messages) {
          totalMessages += metrics.auto_text_messages.extracted_messages.length;
          totalAutomations += metrics.auto_text_messages.extracted_messages.length;
          
          // Better success detection for messages
          const successfulMessages = metrics.auto_text_messages.api_results?.filter((r: any) => 
            r.success === true || 
            (typeof r.success === 'object' && r.success?.success === true) ||
            (r.message_id || r.id) // Message has an ID means it was sent
          ).length || 0;
          successfulAutomations += successfulMessages;
        }
        
        if (metrics.tools_used) {
          totalActions += metrics.tools_used.length;
          totalAutomations += metrics.tools_used.length;
          
          // Improved success detection for tools
          const successfulTools = metrics.tools_used.filter(t => {
            const result = t.tool_result?.toLowerCase() || '';
            return result.includes('success') || 
                   result.includes('completed') || 
                   result.includes('created') ||
                   result.includes('sent') ||
                   (!result.includes('error') && !result.includes('failed') && result.length > 0);
          }).length;
          successfulAutomations += successfulTools;
        }
      }
    });

    setMetrics({
      totalCalls: calls.length,
      completedCalls: completed,
      failedCalls: failed,
      averageCallDuration: callsWithDuration > 0 ? totalDuration / callsWithDuration : 0,
      satisfactionScore: satisfactionCount > 0 ? totalSatisfaction / satisfactionCount : 0,
      automationStats: {
        totalTasks,
        totalMessages,
        totalActions,
        successRate: totalAutomations > 0 ? (successfulAutomations / totalAutomations) * 100 : 0,
      },
    });
  };

  const generateChartData = (calls: ScheduledCall[]) => {
    // Generate all dates in the range for uniform x-axis spacing
    // Add +1 day to the date range for display
    const generateDateRange = (start: Date, end: Date): string[] => {
      const dates: string[] = [];
      const currentDate = new Date(start);
      const endDate = new Date(end);
      
      while (currentDate <= endDate) {
        dates.push(format(currentDate, 'yyyy-MM-dd'));
        currentDate.setDate(currentDate.getDate() + 1);
      }
      
      return dates;
    };

    // Create complete date range with +1 day shift
    const displayStartDate = addDays(dateRange.startDate, 1);
    const displayEndDate = addDays(dateRange.endDate, 1);
    const allDates = generateDateRange(displayStartDate, displayEndDate);
    
    // Call volume by actual call date (prefer call_time over schedule_dt)
    const volumeByDate = new Map();
    
    // Initialize all dates with 0 calls
    allDates.forEach(date => {
      volumeByDate.set(date, 0);
    });
    
    // Count actual calls - shift dates by +1 day for display
    calls.forEach(call => {
      let dateToUse = null;
      
      // Prefer actual call time if available
      if (call.metrics?.call_time) {
        dateToUse = new Date(call.metrics.call_time);
      } else if (call.schedule_dt) {
        dateToUse = new Date(call.schedule_dt);
      }
      
      if (dateToUse && !isNaN(dateToUse.getTime())) {
        // Add +1 day to the date for display
        const shiftedDate = addDays(dateToUse, 1);
        const date = format(shiftedDate, 'yyyy-MM-dd');
        // Only count if the date is within our range
        if (volumeByDate.has(date)) {
          volumeByDate.set(date, (volumeByDate.get(date) || 0) + 1);
        }
      }
    });

    // Call durations with better filtering
    const durations = calls
      .filter(call => call.metrics?.call_duration)
      .map(call => ({
        id: call.id,
        duration: parseDuration(call.metrics?.call_duration || '0:00'),
      }))
      .filter(item => item.duration > 0) // Only include valid durations
      .sort((a, b) => b.duration - a.duration)
      .slice(0, 10);

    // Satisfaction trend by actual call date with uniform spacing
    const satisfactionByDate = new Map();
    const satisfactionCounts = new Map();
    
    // Initialize all dates with 0 for satisfaction
    allDates.forEach(date => {
      satisfactionByDate.set(date, 0);
      satisfactionCounts.set(date, 0);
    });
    
    calls.forEach(call => {
      if (call.metrics?.caller_satisfaction !== undefined) {
        let dateToUse = null;
        
        // Prefer actual call time if available
        if (call.metrics?.call_time) {
          dateToUse = new Date(call.metrics.call_time);
        } else if (call.schedule_dt) {
          dateToUse = new Date(call.schedule_dt);
        }
        
        if (dateToUse && !isNaN(dateToUse.getTime())) {
          // Add +1 day to the date for display
          const shiftedDate = addDays(dateToUse, 1);
          const date = format(shiftedDate, 'yyyy-MM-dd');
          // Only count if the date is within our range
          if (satisfactionByDate.has(date)) {
            const currentTotal = satisfactionByDate.get(date) || 0;
            const currentCount = satisfactionCounts.get(date) || 0;
            satisfactionByDate.set(date, currentTotal + call.metrics.caller_satisfaction);
            satisfactionCounts.set(date, currentCount + 1);
          }
        }
      }
    });

    // Status distribution
    const statusCount = {
      completed: calls.filter(call => call.call_placed).length,
      failed: calls.filter(call => call.call_attempted && !call.call_placed).length,
      pending: calls.filter(call => !call.call_attempted).length,
    };

    // Actions taken distribution with improved success detection
    const actionCounts = new Map<string, { success: number; failed: number }>();

    calls.forEach(call => {
      const metrics = call.metrics;
      if (metrics) {
                  // Count tasks with better success detection
          if (metrics.auto_tasks?.extracted_tasks) {
            const successfulTasks = metrics.auto_tasks.api_results?.filter((r: any) => 
              r.success === true || 
              (typeof r.success === 'object' && r.success?.success === true) ||
              (typeof r === 'object' && r.task?.id)
            ).length || 0;
          const failedTasks = metrics.auto_tasks.extracted_tasks.length - successfulTasks;
          
          actionCounts.set('Create Task', {
            success: (actionCounts.get('Create Task')?.success || 0) + successfulTasks,
            failed: (actionCounts.get('Create Task')?.failed || 0) + failedTasks
          });
        }

        // Count messages with better success detection
        if (metrics.auto_text_messages?.extracted_messages) {
          const successfulMessages = metrics.auto_text_messages.api_results?.filter(r => 
            r.success === true || 
            (typeof r.success === 'object' && r.success?.success === true) ||
            (r.message_id || r.id)
          ).length || 0;
          const failedMessages = metrics.auto_text_messages.extracted_messages.length - successfulMessages;
          
          actionCounts.set('Send Message', {
            success: (actionCounts.get('Send Message')?.success || 0) + successfulMessages,
            failed: (actionCounts.get('Send Message')?.failed || 0) + failedMessages
          });
        }

        // Count specific tools with improved success detection
        if (metrics.tools_used) {
          metrics.tools_used.forEach(tool => {
            const result = tool.tool_result?.toLowerCase() || '';
            const isSuccess = result.includes('success') || 
                             result.includes('completed') || 
                             result.includes('created') ||
                             result.includes('sent') ||
                             (!result.includes('error') && !result.includes('failed') && result.length > 0);
            
            const actionName = tool.tool_name || 'Unknown Tool';
            const current = actionCounts.get(actionName) || { success: 0, failed: 0 };
            actionCounts.set(actionName, {
              success: current.success + (isSuccess ? 1 : 0),
              failed: current.failed + (isSuccess ? 0 : 1)
            });
          });
        }
      }
    });

    // Convert action counts to chart data format
    const actionsTaken = Array.from(actionCounts.entries()).flatMap(([name, counts]) => [
      { name: `${name} (Success)`, value: counts.success, color: '#4caf50' },
      ...(counts.failed > 0 ? [{ name: `${name} (Failed)`, value: counts.failed, color: '#f44336' }] : [])
    ]).filter(item => item.value > 0);

    setChartData({
      callVolume: allDates.map(date => ({
        date,
        calls: volumeByDate.get(date) || 0,
      })), // Already in chronological order
      callDurations: durations,
      satisfactionTrend: allDates.map(date => {
        const total = satisfactionByDate.get(date) || 0;
        const count = satisfactionCounts.get(date) || 0;
        return {
          date,
          satisfaction: count > 0 ? total / count : null, // Use null for days with no satisfaction data
        };
      }), // Already in chronological order
      statusDistribution: [
        { name: 'Completed', value: statusCount.completed, color: '#4caf50' },
        { name: 'Failed', value: statusCount.failed, color: '#f44336' },
        { name: 'Pending', value: statusCount.pending, color: '#ff9800' },
      ],
      actionsTaken,
    });
  };

  // Improved duration parsing to handle multiple formats
  const parseDuration = (duration: string): number => {
    if (!duration || typeof duration !== 'string') return 0;
    
    // Handle different duration formats
    const trimmed = duration.trim();
    
    // Format: "HH:MM:SS" or "MM:SS"
    if (trimmed.includes(':')) {
      const parts = trimmed.split(':').map(p => parseInt(p, 10));
      
      if (parts.length === 3) {
        // HH:MM:SS format
        const [hours, minutes, seconds] = parts;
        return (hours * 60) + minutes + (seconds / 60);
      } else if (parts.length === 2) {
        // MM:SS format
        const [minutes, seconds] = parts;
        return minutes + (seconds / 60);
      }
    }
    
    // Format: just seconds (number)
    const seconds = parseFloat(trimmed);
    if (!isNaN(seconds)) {
      return seconds / 60; // Convert to minutes
    }
    
    // Format: "X minutes Y seconds" or similar text formats
    const minutesMatch = trimmed.match(/(\d+)\s*(?:minutes?|mins?|m)/i);
    const secondsMatch = trimmed.match(/(\d+)\s*(?:seconds?|secs?|s)/i);
    
    let totalMinutes = 0;
    if (minutesMatch) totalMinutes += parseInt(minutesMatch[1], 10);
    if (secondsMatch) totalMinutes += parseInt(secondsMatch[1], 10) / 60;
    
    return totalMinutes;
  };

  const formatDuration = (minutes: number): string => {
    if (minutes < 1) {
      const seconds = Math.round(minutes * 60);
      return `${seconds}s`;
    }
    
    const mins = Math.floor(minutes);
    const secs = Math.round((minutes - mins) * 60);
    return mins > 0 ? `${mins}m ${secs}s` : `${secs}s`;
  };

  const handleFilterChange = (name: keyof typeof filters, value: any) => {
    setFilters(prev => ({ ...prev, [name]: value }));
  };

  const handleDateRangeChange = (field: 'startDate' | 'endDate', date: Date | null) => {
    if (date) {
      setDateRange(prev => ({ ...prev, [field]: date }));
    }
  };

  const resetFilters = () => {
    setFilters({
      status: '',
      agent_id: '',
      from_number: '',
      to_number: '',
      is_reminder: '',
    });
    setDateRange({
      startDate: subDays(new Date(), 7),
      endDate: new Date()
    });
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error">{error}</Alert>;
  }

  return (
    <Box sx={{ width: '100%' }}>
      <Stack spacing={3}>
        {/* Filters Section */}
        <Paper
          elevation={0}
          sx={{
            borderRadius: 2,
            border: '1px solid',
            borderColor: alpha('#000', 0.08),
          }}
        >
          <Box 
            sx={{ 
              p: 2, 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center',
              borderBottom: filtersVisible ? `1px solid ${alpha('#000', 0.08)}` : 'none',
              cursor: 'pointer'
            }}
            onClick={() => setFiltersVisible(!filtersVisible)}
          >
            <Typography variant="h6" sx={{ fontWeight: 500 }}>
              Filters
            </Typography>
            <IconButton size="small">
              {filtersVisible ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </IconButton>
          </Box>

          {filtersVisible && (
            <Box sx={{ p: 1.5 }}>
              <Stack spacing={1.5}>
                <Grid container spacing={1.5}>
                  <Grid item xs={12} sm={6} md={2}>
                    <DatePicker
                      label="From Date"
                      value={dateRange.startDate}
                      onChange={(date) => handleDateRangeChange('startDate', date)}
                      slotProps={{
                        textField: {
                          fullWidth: true,
                          size: "small",
                          sx: {
                            '& .MuiOutlinedInput-root': {
                              height: '40px',
                              borderRadius: 1,
                              backgroundColor: alpha('#000', 0.02),
                            },
                          },
                        },
                      }}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6} md={2}>
                    <DatePicker
                      label="To Date"
                      value={dateRange.endDate}
                      onChange={(date) => handleDateRangeChange('endDate', date)}
                      slotProps={{
                        textField: {
                          fullWidth: true,
                          size: "small",
                          sx: {
                            '& .MuiOutlinedInput-root': {
                              height: '40px',
                              borderRadius: 1,
                              backgroundColor: alpha('#000', 0.02),
                            },
                          },
                        },
                      }}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6} md={2}>
                    <AgentSelect
                      value={filters.agent_id ? parseInt(filters.agent_id.toString()) : null}
                      onChange={(value) => handleFilterChange('agent_id', value)}
                      label="Agent"
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          height: '40px',
                          borderRadius: 1,
                          backgroundColor: alpha('#000', 0.02),
                        },
                      }}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6} md={2}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Reminder</InputLabel>
                      <Select
                        value={filters.is_reminder}
                        onChange={(e) => handleFilterChange('is_reminder', e.target.value)}
                        label="Reminder"
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            height: '40px',
                            borderRadius: 1,
                            backgroundColor: alpha('#000', 0.02),
                          },
                        }}
                      >
                        <MenuItem value="">All</MenuItem>
                        <MenuItem value="true">Reminders Only</MenuItem>
                        <MenuItem value="false">Non-Reminders Only</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>

                  <Grid item xs={12} sm={6} md={2}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Status</InputLabel>
                      <Select
                        value={filters.status}
                        onChange={(e) => handleFilterChange('status', e.target.value)}
                        label="Status"
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            height: '40px',
                            borderRadius: 1,
                            backgroundColor: alpha('#000', 0.02),
                          },
                        }}
                      >
                        <MenuItem value="">All</MenuItem>
                        <MenuItem value="pending">Pending</MenuItem>
                        <MenuItem value="attempted">Attempted</MenuItem>
                        <MenuItem value="placed">Completed</MenuItem>
                        <MenuItem value="failed">Failed</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>

                  <Grid item xs={12} sm={6} md={2}>
                    <Stack direction="row" spacing={1} sx={{ height: '100%', alignItems: 'center' }}>
                      <Button
                        variant="outlined"
                        onClick={resetFilters}
                        size="small"
                        sx={{
                          borderStyle: 'dashed',
                          color: 'text.secondary',
                          flex: 1,
                          height: '40px', // Match input height
                        }}
                      >
                        Reset
                      </Button>
                      <IconButton
                        onClick={fetchData}
                        size="small"
                        sx={{
                          backgroundColor: 'primary.main',
                          color: 'white',
                          height: '40px',
                          width: '40px',
                          '&:hover': {
                            backgroundColor: 'primary.dark',
                          },
                        }}
                      >
                        <RefreshIcon />
                      </IconButton>
                    </Stack>
                  </Grid>
                </Grid>
              </Stack>
            </Box>
          )}
        </Paper>

        {/* Key Metrics */}
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={4}>
            <Paper sx={{ p: 2, height: '100%' }}>
              <Typography variant="subtitle2" color="text.secondary">Total Calls</Typography>
              <Typography variant="h4">{metrics.totalCalls}</Typography>
              <Box sx={{ mt: 1 }}>
                <Typography variant="body2" color="success.main">
                  {metrics.totalCalls > 0 ? ((metrics.completedCalls / metrics.totalCalls) * 100).toFixed(1) : 0}% Success Rate
                </Typography>
              </Box>
            </Paper>
          </Grid>
        </Grid>

        {/* Charts */}
        <Grid container spacing={3}>
          {/* Call Volume Trend */}
          <Grid item xs={12} md={8}>
            <Paper sx={{ p: 2, height: '400px' }}>
              <Typography variant="h6" gutterBottom>Call Volume Trend</Typography>
              <ResponsiveContainer width="100%" height="90%">
                <LineChart data={chartData.callVolume}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis 
                    dataKey="date" 
                    tickFormatter={(value) => {
                      // Format date for display (show every few days if too many)
                      const date = new Date(value);
                      return format(date, 'MMM d');
                    }}
                    interval="preserveStartEnd"
                  />
                  <YAxis />
                  <Tooltip 
                    labelFormatter={(value) => {
                      const date = new Date(value);
                      return format(date, 'MMM d, yyyy');
                    }}
                  />
                  <Legend />
                  <Line 
                    type="monotone" 
                    dataKey="calls" 
                    stroke="#2196f3" 
                    name="Number of Calls"
                    strokeWidth={2}
                    dot={{ r: 3 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </Paper>
          </Grid>

          {/* Status Distribution */}
          <Grid item xs={12} md={4}>
            <Paper sx={{ p: 2, height: '400px' }}>
              <Typography variant="h6" gutterBottom>Call Status Distribution</Typography>
              <ResponsiveContainer width="100%" height="90%">
                <PieChart>
                  <Pie
                    data={chartData.statusDistribution}
                    dataKey="value"
                    nameKey="name"
                    cx="50%"
                    cy="50%"
                    outerRadius={80}
                    label
                  >
                    {chartData.statusDistribution.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            </Paper>
          </Grid>

          {/* Satisfaction Trend */}
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 2, height: '400px' }}>
              <Typography variant="h6" gutterBottom>Satisfaction Trend</Typography>
              <ResponsiveContainer width="100%" height="90%">
                <LineChart data={chartData.satisfactionTrend}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis 
                    dataKey="date"
                    tickFormatter={(value) => {
                      const date = new Date(value);
                      return format(date, 'MMM d');
                    }}
                    interval="preserveStartEnd"
                  />
                  <YAxis domain={[0, 10]} />
                  <Tooltip 
                    labelFormatter={(value) => {
                      const date = new Date(value);
                      return format(date, 'MMM d, yyyy');
                    }}
                    formatter={(value) => {
                      return value !== null && typeof value === 'number' ? [value.toFixed(1), 'Satisfaction Score'] : ['No data', ''];
                    }}
                  />
                  <Legend />
                  <Line 
                    type="monotone" 
                    dataKey="satisfaction" 
                    stroke="#4caf50" 
                    name="Satisfaction Score"
                    strokeWidth={2}
                    dot={{ r: 3 }}
                    connectNulls={false}
                  />
                </LineChart>
              </ResponsiveContainer>
            </Paper>
          </Grid>

          {/* Actions Taken Distribution */}
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 2, height: '400px' }}>
              <Typography variant="h6" gutterBottom>Actions Taken Distribution</Typography>
              <ResponsiveContainer width="100%" height="90%">
                <BarChart data={chartData.actionsTaken} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis type="number" />
                  <YAxis dataKey="name" type="category" width={150} />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="value" name="Count">
                    {chartData.actionsTaken.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </Paper>
          </Grid>
        </Grid>
      </Stack>
    </Box>
  );
};

export default CallAnalytics; 